import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Box,
  Button,
  Header,
  SpaceBetween,
  Modal,
  Form,
  FormField,
  Input,
  DatePicker,
  Checkbox,
  Badge,
  Alert,
  ColumnLayout,
  Link,
} from '@cloudscape-design/components';
import AppLayoutWrapper from './AppLayoutWrapper';
import { useAuth } from '../AuthContext';
import { useNotifications } from './NotificationContext';
import axios from 'axios';

const SCOPE_OPTIONS = [
  'admin:read',
  'admin:write',
  'admin:delete',
  'billing:read',
  'billing:write',
];

export default function ApiManagement() {
  const { user, isSuperAdmin } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const token = user?.access_token || user?.token;

  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [revokeModalVisible, setRevokeModalVisible] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState(null);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [newKeyModalVisible, setNewKeyModalVisible] = useState(false);

  // Form state
  const [keyName, setKeyName] = useState('');
  const [keyScopes, setKeyScopes] = useState([]);
  const [keyIps, setKeyIps] = useState('');
  const [keyExpiry, setKeyExpiry] = useState('');
  const [keyNoExpiry, setKeyNoExpiry] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchApiKeys = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get('/api/admin/api-keys/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        setApiKeys(res.data.data || []);
      }
    } catch (err) {
      notifyError('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, [token, notifyError]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchApiKeys();
    }
  }, [isSuperAdmin, fetchApiKeys]);

  const handleCreateKey = async () => {
    if (!keyName.trim()) {
      notifyError('Key name is required');
      return;
    }
    setCreating(true);
    try {
      const body = {
        name: keyName.trim(),
        scopes: keyScopes,
        allowed_ips: keyIps.trim() ? keyIps.split(',').map(ip => ip.trim()).filter(Boolean) : [],
      };
      if (!keyNoExpiry && keyExpiry) {
        body.expires_at = `${keyExpiry}T23:59:59Z`;
      }
      const res = await axios.post('/api/admin/api-keys/create/', body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        setNewKeyResult(res.data.data);
        setNewKeyModalVisible(true);
        setCreateModalVisible(false);
        setKeyName('');
        setKeyScopes([]);
        setKeyIps('');
        setKeyExpiry('');
        setKeyNoExpiry(true);
        fetchApiKeys();
        notifySuccess('API key created successfully');
      }
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeKey = async () => {
    if (!keyToRevoke) return;
    try {
      const res = await axios.delete(`/api/admin/api-keys/${keyToRevoke.id}/revoke/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === 'success') {
        notifySuccess(`API key "${keyToRevoke.name}" revoked`);
        fetchApiKeys();
      }
    } catch (err) {
      notifyError('Failed to revoke API key');
    } finally {
      setRevokeModalVisible(false);
      setKeyToRevoke(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <AppLayoutWrapper
        header={{ title: 'API Management' }}
        breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'API Management' }]}
      >
        <Alert type="error" header="Access Denied">
          You need super admin privileges to access this page.
        </Alert>
      </AppLayoutWrapper>
    );
  }

  return (
    <AppLayoutWrapper
      header={{
        title: 'API Management',
        description: 'Create and manage API keys for external application access.',
        actions: (
          <Button variant="primary" onClick={() => setCreateModalVisible(true)}>
            Create API Key
          </Button>
        ),
      }}
      breadcrumbs={[{ text: 'Home', href: '/' }, { text: 'Super Admin', href: '/admin/api-management' }, { text: 'API Management' }]}
    >
      <SpaceBetween size="l">
        <Alert type="info" header="About API Keys">
          API keys allow external applications to access admin APIs. Use the header
          <code>{' Authorization: Api-Key <your-key> '}</code>
          or <code>{' X-API-Key: <your-key> '}</code>. The raw key is shown only once at creation — store it securely.
        </Alert>

        <Table
          columnDefinitions={[
            {
              id: 'name',
              header: 'Name',
              cell: (item) => item.name,
              sortingField: 'name',
            },
            {
              id: 'key_prefix',
              header: 'Key Prefix',
              cell: (item) => <code>{item.key_prefix}…</code>,
            },
            {
              id: 'scopes',
              header: 'Scopes',
              cell: (item) =>
                item.scopes?.length > 0 ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    {item.scopes.map((s) => (
                      <Badge key={s} color="blue">{s}</Badge>
                    ))}
                  </SpaceBetween>
                ) : (
                  <Badge color="grey">All scopes</Badge>
                ),
            },
            {
              id: 'allowed_ips',
              header: 'IP Restriction',
              cell: (item) =>
                item.allowed_ips?.length > 0 ? (
                  <SpaceBetween direction="horizontal" size="xs">
                    {item.allowed_ips.map((ip) => (
                      <Badge key={ip} color="green">{ip}</Badge>
                    ))}
                  </SpaceBetween>
                ) : (
                  <Badge color="grey">Any IP</Badge>
                ),
            },
            {
              id: 'status',
              header: 'Status',
              cell: (item) =>
                item.is_active ? (
                  <Badge color="green">Active</Badge>
                ) : (
                  <Badge color="red">Revoked</Badge>
                ),
            },
            {
              id: 'expires_at',
              header: 'Expires',
              cell: (item) =>
                item.expires_at ? (
                  new Date(item.expires_at).toLocaleDateString()
                ) : (
                  <Badge color="grey">Never</Badge>
                ),
            },
            {
              id: 'last_used_at',
              header: 'Last Used',
              cell: (item) =>
                item.last_used_at ? new Date(item.last_used_at).toLocaleString() : 'Never',
            },
            {
              id: 'created_at',
              header: 'Created',
              cell: (item) => new Date(item.created_at).toLocaleDateString(),
            },
            {
              id: 'actions',
              header: 'Actions',
              cell: (item) =>
                item.is_active ? (
                  <Button
                    variant="normal"
                    onClick={() => {
                      setKeyToRevoke(item);
                      setRevokeModalVisible(true);
                    }}
                  >
                    Revoke
                  </Button>
                ) : (
                  '—'
                ),
            },
          ]}
          items={apiKeys}
          loading={loading}
          loadingText="Loading API keys…"
          trackBy="id"
          empty={
            <Box textAlign="center" color="inherit">
              <b>No API keys</b>
              <Box padding={{ top: 's' }} variant="p" color="text-body-secondary">
                Create an API key to allow external applications to access the admin API.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={apiKeys.length > 0 ? `(${apiKeys.length})` : ''}
              actions={
                <Button iconName="refresh" onClick={fetchApiKeys}>
                  Refresh
                </Button>
              }
            >
              API Keys
            </Header>
          }
        />
      </SpaceBetween>

      {/* Create API Key Modal */}
      <Modal
        visible={createModalVisible}
        onDismiss={() => setCreateModalVisible(false)}
        header="Create API Key"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setCreateModalVisible(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateKey} loading={creating}>
              Create
            </Button>
          </SpaceBetween>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="Key Name" description="A human-readable label for this API key.">
              <Input
                value={keyName}
                onChange={({ detail }) => setKeyName(detail.value)}
                placeholder="e.g. My External App"
              />
            </FormField>

            <FormField label="Scopes" description="Optional scope restrictions. Leave empty for all scopes.">
              <SpaceBetween size="s">
                {SCOPE_OPTIONS.map((scope) => (
                  <Checkbox
                    key={scope}
                    checked={keyScopes.includes(scope)}
                    onChange={({ detail }) => {
                      if (detail.checked) {
                        setKeyScopes([...keyScopes, scope]);
                      } else {
                        setKeyScopes(keyScopes.filter((s) => s !== scope));
                      }
                    }}
                  >
                    {scope}
                  </Checkbox>
                ))}
              </SpaceBetween>
            </FormField>

            <FormField
              label="IP Whitelist"
              description="Comma-separated list of allowed IPs or CIDR ranges. Leave empty to allow any IP."
            >
              <Input
                value={keyIps}
                onChange={({ detail }) => setKeyIps(detail.value)}
                placeholder="e.g. 192.168.1.10, 10.0.0.0/24"
              />
            </FormField>

            <FormField label="Expiration">
              <SpaceBetween size="s">
                <Checkbox
                  checked={keyNoExpiry}
                  onChange={({ detail }) => setKeyNoExpiry(detail.checked)}
                >
                  No expiration (key never expires)
                </Checkbox>
                {!keyNoExpiry && (
                  <DatePicker
                    value={keyExpiry}
                    onChange={({ detail }) => setKeyExpiry(detail.value)}
                    placeholder="Select expiration date"
                    isDateEnabled={(date) => new Date(date) > new Date()}
                  />
                )}
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        </Form>
      </Modal>

      {/* New Key Result Modal */}
      <Modal
        visible={newKeyModalVisible}
        onDismiss={() => setNewKeyModalVisible(false)}
        header="API Key Created"
        closeAriaLabel="Close dialog"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="primary"
              onClick={() => {
                if (newKeyResult?.raw_key) {
                  navigator.clipboard.writeText(newKeyResult.raw_key);
                  notifySuccess('API key copied to clipboard');
                }
              }}
            >
              Copy Key
            </Button>
            <Button variant="link" onClick={() => setNewKeyModalVisible(false)}>
              Close
            </Button>
          </SpaceBetween>
        }
      >
        <Alert type="warning" header="Store this key securely">
          The raw API key is shown only once. You will not be able to see it again.
        </Alert>
        <Box padding={{ top: 'l' }}>
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="h5">Name</Box>
              <div>{newKeyResult?.name}</div>
            </div>
            <div>
              <Box variant="h5">Key Prefix</Box>
              <div><code>{newKeyResult?.key_prefix}…</code></div>
            </div>
            <div>
              <Box variant="h5">Expires</Box>
              <div>{newKeyResult?.expires_at ? new Date(newKeyResult.expires_at).toLocaleString() : 'Never'}</div>
            </div>
            <div>
              <Box variant="h5">Scopes</Box>
              <div>{newKeyResult?.scopes?.length > 0 ? newKeyResult.scopes.join(', ') : 'All'}</div>
            </div>
            <div>
              <Box variant="h5">IP Restriction</Box>
              <div>{newKeyResult?.allowed_ips?.length > 0 ? newKeyResult.allowed_ips.join(', ') : 'Any IP'}</div>
            </div>
          </ColumnLayout>
          <Box padding={{ top: 'l' }}>
            <FormField label="Raw API Key">
              <Input
                value={newKeyResult?.raw_key || ''}
                readOnly
                copyButton
              />
            </FormField>
          </Box>
        </Box>
      </Modal>

      {/* Revoke Confirmation Modal */}
      <Modal
        visible={revokeModalVisible}
        onDismiss={() => {
          setRevokeModalVisible(false);
          setKeyToRevoke(null);
        }}
        header="Revoke API Key"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={() => {
                setRevokeModalVisible(false);
                setKeyToRevoke(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRevokeKey}>
              Revoke
            </Button>
          </SpaceBetween>
        }
      >
        <Alert type="warning" header="Confirm Revocation">
          Are you sure you want to revoke the API key <strong>"{keyToRevoke?.name}"</strong>?
          External applications using this key will immediately lose access.
        </Alert>
      </Modal>
    </AppLayoutWrapper>
  );
}
